class LunaProductExperience extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.autoplayTimers = new Set();
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.prefersReducedMotion.matches;
    this.autoplayInterval = Math.max(Number(this.dataset.autoplayInterval) || 5000, 3000);
    this.initializeModeSwitcher();
    this.initializeStepSwitcher();
    this.initializeTutorialVideo();
    this.querySelectorAll('[data-luna-carousel]').forEach((carousel) => this.initializeCarousel(carousel));
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.autoplayTimers.forEach((timer) => window.clearInterval(timer));
    this.autoplayTimers.clear();
  }

  initializeModeSwitcher() {
    const component = this.querySelector('[data-luna-mode-component]');
    if (!component) return;

    const panels = Array.from(component.querySelectorAll('[data-luna-mode-panel]'));
    const tabs = Array.from(component.querySelectorAll('[data-luna-mode-tab]'));
    const track = component.querySelector('[data-luna-mode-track]');
    if (!panels.length || !track) return;

    let activeIndex = Math.max(panels.findIndex((panel) => panel.classList.contains('is-active')), 0);
    let autoplayTimer = null;

    const setActiveMode = (requestedIndex, shouldFocus = false) => {
      activeIndex = (requestedIndex + panels.length) % panels.length;
      track.style.setProperty('--luna-mode-index', String(activeIndex));

      panels.forEach((panel, index) => {
        const isActive = index === activeIndex;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', String(!isActive));
      });

      tabs.forEach((tab, index) => {
        const isActive = index === activeIndex;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      if (shouldFocus) tabs[activeIndex]?.focus();
    };

    const stopAutoplay = () => {
      if (!autoplayTimer) return;
      window.clearInterval(autoplayTimer);
      this.autoplayTimers.delete(autoplayTimer);
      autoplayTimer = null;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!this.autoplayEnabled || panels.length < 2) return;
      autoplayTimer = window.setInterval(() => setActiveMode(activeIndex + 1), this.autoplayInterval);
      this.autoplayTimers.add(autoplayTimer);
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        setActiveMode(index);
        startAutoplay();
      }, { signal: this.abortController.signal });
    });

    component.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      setActiveMode(activeIndex + (event.key === 'ArrowRight' ? 1 : -1), true);
      startAutoplay();
    }, { signal: this.abortController.signal });

    this.bindAutoplayPause(component, stopAutoplay, startAutoplay);
    this.bindSwipe(track, () => setActiveMode(activeIndex + 1), () => setActiveMode(activeIndex - 1));
    setActiveMode(activeIndex);
    startAutoplay();
  }

  initializeStepSwitcher() {
    const component = this.querySelector('[data-luna-step-component]');
    if (!component) return;

    const tabs = Array.from(component.querySelectorAll('[data-luna-step-tab]'));
    const panels = Array.from(component.querySelectorAll('[data-luna-step-panel]'));

    const setActiveStep = (index, shouldFocus = false) => {
      tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', String(!isActive));
      });

      if (shouldFocus) tabs[index]?.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => setActiveStep(index), { signal: this.abortController.signal });
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const nextIndex = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        setActiveStep(nextIndex, true);
      }, { signal: this.abortController.signal });
    });

    if (tabs.length) setActiveStep(0);
  }

  initializeTutorialVideo() {
    const poster = this.querySelector('[data-luna-tutorial-poster]');
    const media = this.querySelector('[data-luna-tutorial-media]');
    if (!poster || !media) return;

    poster.addEventListener('click', () => {
      const provider = poster.dataset.videoProvider;
      const videoId = poster.dataset.videoId;
      if (!videoId || (provider !== 'youtube' && provider !== 'vimeo')) return;

      const iframe = document.createElement('iframe');
      iframe.className = 'luna-product-experience__tutorial-video';
      iframe.title = 'Luna quick start tutorial';
      iframe.allowFullscreen = true;
      iframe.allow = provider === 'vimeo'
        ? 'autoplay; fullscreen; picture-in-picture'
        : 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.src = provider === 'vimeo'
        ? `https://player.vimeo.com/video/${encodeURIComponent(videoId)}?autoplay=1&muted=1&playsinline=1`
        : `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1`;

      media.replaceChildren(iframe);
    }, { signal: this.abortController.signal });
  }

  initializeCarousel(carousel) {
    const viewport = carousel.querySelector('.luna-product-experience__carousel-viewport');
    const track = carousel.querySelector('[data-luna-carousel-track]');
    const slides = Array.from(carousel.querySelectorAll('[data-luna-carousel-slide]'));
    const previousButton = carousel.querySelector('[data-luna-carousel-prev]');
    const nextButton = carousel.querySelector('[data-luna-carousel-next]');
    const dotsContainer = carousel.querySelector('[data-luna-carousel-dots]');
    const controls = carousel.querySelector('.luna-product-experience__carousel-controls');
    if (!viewport || !track || !slides.length) return;

    let currentIndex = 0;
    let visibleSlides = 1;
    let maximumIndex = 0;
    let autoplayTimer = null;
    let dots = [];

    const getVisibleSlides = () => {
      // 与 Instant 的核心移动端断点保持一致，避免 576–749px 区间提前切换为手机列数。
      const requested = window.innerWidth <= 575 ? carousel.dataset.slidesMobile : carousel.dataset.slidesDesktop;
      return Math.max(1, Math.min(Number(requested) || 1, slides.length));
    };

    const updateControls = () => {
      dots.forEach((dot, index) => {
        const isActive = index === currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
      if (previousButton) previousButton.disabled = maximumIndex === 0;
      if (nextButton) nextButton.disabled = maximumIndex === 0;
      if (controls) controls.hidden = maximumIndex === 0;
    };

    const goToSlide = (requestedIndex) => {
      if (!maximumIndex) {
        currentIndex = 0;
      } else if (requestedIndex < 0) {
        currentIndex = maximumIndex;
      } else if (requestedIndex > maximumIndex) {
        currentIndex = 0;
      } else {
        currentIndex = requestedIndex;
      }

      const targetSlide = slides[currentIndex];
      const offset = targetSlide ? targetSlide.offsetLeft : 0;
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      updateControls();
    };

    const buildDots = () => {
      if (!dotsContainer) return;
      dotsContainer.replaceChildren();
      dots = Array.from({ length: maximumIndex + 1 }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'luna-product-experience__dot';
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.addEventListener('click', () => {
          goToSlide(index);
          startAutoplay();
        }, { signal: this.abortController.signal });
        dotsContainer.append(dot);
        return dot;
      });
    };

    const calculateLayout = () => {
      visibleSlides = getVisibleSlides();
      maximumIndex = Math.max(slides.length - visibleSlides, 0);
      currentIndex = Math.min(currentIndex, maximumIndex);
      carousel.style.setProperty('--luna-carousel-slides', String(visibleSlides));
      buildDots();
      window.requestAnimationFrame(() => goToSlide(currentIndex));
    };

    const stopAutoplay = () => {
      if (!autoplayTimer) return;
      window.clearInterval(autoplayTimer);
      this.autoplayTimers.delete(autoplayTimer);
      autoplayTimer = null;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!this.autoplayEnabled || maximumIndex < 1) return;
      autoplayTimer = window.setInterval(() => goToSlide(currentIndex + 1), this.autoplayInterval);
      this.autoplayTimers.add(autoplayTimer);
    };

    previousButton?.addEventListener('click', () => {
      goToSlide(currentIndex - 1);
      startAutoplay();
    }, { signal: this.abortController.signal });

    nextButton?.addEventListener('click', () => {
      goToSlide(currentIndex + 1);
      startAutoplay();
    }, { signal: this.abortController.signal });

    this.bindAutoplayPause(carousel, stopAutoplay, startAutoplay);
    this.bindSwipe(viewport, () => {
      goToSlide(currentIndex + 1);
      startAutoplay();
    }, () => {
      goToSlide(currentIndex - 1);
      startAutoplay();
    });

    let resizeFrame = null;
    window.addEventListener('resize', () => {
      if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        calculateLayout();
        startAutoplay();
      });
    }, { signal: this.abortController.signal });

    calculateLayout();
    startAutoplay();
  }

  bindAutoplayPause(component, stopAutoplay, startAutoplay) {
    component.addEventListener('mouseenter', stopAutoplay, { signal: this.abortController.signal });
    component.addEventListener('mouseleave', startAutoplay, { signal: this.abortController.signal });
    component.addEventListener('focusin', stopAutoplay, { signal: this.abortController.signal });
    component.addEventListener('focusout', (event) => {
      if (!component.contains(event.relatedTarget)) startAutoplay();
    }, { signal: this.abortController.signal });
  }

  bindSwipe(target, onSwipeLeft, onSwipeRight) {
    let pointerStartX = null;

    target.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerStartX = event.clientX;
    }, { signal: this.abortController.signal });

    target.addEventListener('pointerup', (event) => {
      if (pointerStartX === null) return;
      const distance = event.clientX - pointerStartX;
      pointerStartX = null;
      if (Math.abs(distance) < 42) return;
      if (distance < 0) onSwipeLeft();
      if (distance > 0) onSwipeRight();
    }, { signal: this.abortController.signal });

    target.addEventListener('pointercancel', () => {
      pointerStartX = null;
    }, { signal: this.abortController.signal });
  }

}

if (!customElements.get('luna-product-experience')) {
  customElements.define('luna-product-experience', LunaProductExperience);
}
